import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminTrack } from '../lib/types';
import { Banner, Field, IconAction, Text, Toggle, icons, slugify } from './ui';
import { api } from '../lib/api';
import { useAdminContent } from './useAdminContent';
import { EditModal } from './EditModal';
import { useConfirm } from '../components/ConfirmDialog';
import { Loading } from '../components/Loading';

/** Tracks, their order, and a count of what each one holds. */
export function TracksAdmin() {
  const { bundle, loading, error, note, run } = useAdminContent();
  const { confirm, dialog } = useConfirm();
  const [draft, setDraft] = useState<Partial<AdminTrack> | null>(null);

  if (loading) return <Loading />;
  if (!bundle) return <Banner error={error ?? 'Could not load content.'} />;

  const lessonCount = (trackId: string) => bundle.lessons.filter((l) => l.trackId === trackId).length;

  // Reordering writes straight through — there is no draft to discard — so it asks
  // first, like every other write in the console.
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= bundle.tracks.length) return;
    const ids = bundle.tracks.map((t) => t._id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    void (async () => {
      const ok = await confirm({
        title: `Move “${bundle.tracks[i].name}” ${d < 0 ? 'up' : 'down'}?`,
        body: 'Track order is the order vendors read the library in. This saves as soon as you confirm.',
        confirmLabel: 'Save new order',
      });
      if (ok) await run(() => api.reorderTracks(ids));
    })();
  };

  async function save() {
    if (!draft?.name?.trim()) return;

    const confirmed = await confirm({
      title: draft._id ? 'Save changes to this track?' : 'Create this track?',
      body: draft.published ?? true
        ? 'It is marked visible, so vendors will see the change immediately.'
        : 'It is a draft, so vendors will not see it until you publish.',
      confirmLabel: draft._id ? 'Save changes' : 'Create track',
    });
    if (!confirmed) return;

    const body = {
      slug: draft.slug?.trim() || slugify(draft.name),
      name: draft.name.trim(),
      blurb: draft.blurb ?? '',
      iconPath: draft.iconPath ?? '',
      tint: draft.tint || '#FEF2E7',
      published: draft.published ?? true,
    };
    const ok = draft._id
      ? await run(() => api.updateTrack(draft._id!, body), 'Track saved.')
      : await run(() => api.createTrack(body), 'Track created.');
    if (ok) setDraft(null);
  }

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Content</div>
      <h1 className="vu-h1">Tracks</h1>
      <p className="vu-lede" style={{ maxWidth: '60ch' }}>
        The eight shelves of the reference library. Order here is the order vendors see in
        the sidebar.
      </p>

      <Banner error={error} note={note} />

      <div className="vu-card" style={{ padding: 0, overflow: 'hidden', marginTop: 'var(--space-6)' }}>
        <div className="vu-tablewrap">
        <table className="vu-table">
          <thead>
            <tr style={{ background: 'var(--surface-sunken)' }}>
              <th style={{ paddingLeft: 'var(--space-5)' }}>Track</th>
              <th style={{ width: 110 }}>Lessons</th>
              <th style={{ width: 110 }}>State</th>
              <th style={{ width: 190, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bundle.tracks.map((t, i) => (
              <tr key={t._id}>
                <td style={{ paddingLeft: 'var(--space-5)' }}>
                  <div style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)' }}>{t.name}</div>
                  <div className="vu-subtle" style={{ fontSize: 'var(--fs-xs)' }}>/{t.slug}</div>
                </td>
                <td>
                  <Link to={`/admin/tracks/${t._id}`}>{lessonCount(t._id)} lesson(s)</Link>
                </td>
                <td>
                  {t.published ? (
                    <span style={{ color: 'var(--green-500)', fontWeight: 600, fontSize: 'var(--fs-sm)' }}>Live</span>
                  ) : (
                    <span className="vu-subtle" style={{ fontSize: 'var(--fs-sm)' }}>Draft</span>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                    <IconAction label="Move up" onClick={() => move(i, -1)} disabled={i === 0}>
                      {icons.up}
                    </IconAction>
                    <IconAction label="Move down" onClick={() => move(i, 1)} disabled={i === bundle.tracks.length - 1}>
                      {icons.down}
                    </IconAction>
                    <IconAction label="Edit track" onClick={() => setDraft(t)}>{icons.edit}</IconAction>
                    <IconAction
                      label={t.published ? 'Unpublish — hide from vendors' : 'Publish — show to vendors'}
                      onClick={() => {
                        void (async () => {
                          const ok = await confirm({
                            title: t.published ? `Hide “${t.name}” from vendors?` : `Publish “${t.name}”?`,
                            body: t.published
                              ? 'The track and its lessons disappear from every vendor’s library. Their progress is kept.'
                              : 'The track becomes visible to every approved vendor immediately.',
                            confirmLabel: t.published ? 'Unpublish' : 'Publish',
                          });
                          if (ok) await run(() => api.updateTrack(t._id, { published: !t.published }));
                        })();
                      }}
                    >
                      {t.published ? icons.published : icons.hidden}
                    </IconAction>
                    <IconAction
                      label="Delete track"
                      danger
                      onClick={() => {
                        void (async () => {
                          // The API refuses to delete a track that still has lessons, so
                          // this is about intent, not about preventing an orphan.
                          const ok = await confirm({
                            title: `Delete “${t.name}”?`,
                            body: 'This cannot be undone. A track that still has lessons cannot be deleted — move or delete those first.',
                            confirmLabel: 'Delete track',
                            destructive: true,
                          });
                          if (ok) await run(() => api.deleteTrack(t._id));
                        })();
                      }}
                    >
                      {icons.trash}
                    </IconAction>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <button
        type="button"
        className="vu-btn"
        style={{ marginTop: 'var(--space-5)' }}
        onClick={() => setDraft({ published: true, tint: '#FEF2E7' })}
      >
        + New track
      </button>

      <EditModal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?._id ? 'Edit track' : 'New track'}
        description="Changes go live for vendors as soon as you save."
        footer={
          <>
            <button type="button" className="vu-btn vu-btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
            <button type="button" className="vu-btn" onClick={save}>Save</button>
          </>
        }
      >
        {draft && (
          <>
            <Field label="Name">
              <Text value={draft.name ?? ''} onChange={(name) => setDraft({ ...draft, name })} />
            </Field>
            <Field label="Slug" hint="Appears in the URL. Leave blank to derive it from the name.">
              <Text
                value={draft.slug ?? ''}
                onChange={(slug) => setDraft({ ...draft, slug })}
                placeholder={draft.name ? slugify(draft.name) : 'listings-that-sell'}
              />
            </Field>
            <Field label="Blurb">
              <Text value={draft.blurb ?? ''} onChange={(blurb) => setDraft({ ...draft, blurb })} />
            </Field>
            <Field label="Tint" hint="Background colour of the track's icon tile.">
              <Text value={draft.tint ?? '#FEF2E7'} onChange={(tint) => setDraft({ ...draft, tint })} />
            </Field>
            <Field label="Icon path" hint="The `d` attribute of a 24×24 SVG path. Blank is fine.">
              <Text value={draft.iconPath ?? ''} onChange={(iconPath) => setDraft({ ...draft, iconPath })} />
            </Field>
            <Toggle
              checked={draft.published ?? true}
              onChange={(published) => setDraft({ ...draft, published })}
              label="Visible to vendors"
            />
          </>
        )}
      </EditModal>

      {dialog}
    </div>
  );
}
